import React, { useState, useMemo, useRef, useEffect } from 'react';
import {
  LocalFireDepartmentOutlined,
  WhatshotOutlined,
  PsychologyOutlined,
  ShieldOutlined,
  ForestOutlined,
  WaterOutlined,
  BoltOutlined,
  TrendingUpOutlined,
  SearchOutlined,
  RefreshOutlined,
} from '@mui/icons-material';
import { CloseOutlined } from '@mui/icons-material';

import StatCard from '../../atoms/StatCard/StatCard';
import DateRangePicker from '../../molecules/DateRangePicker/DateRangePicker';
import ViewSwitcher from '../../atoms/ViewSwitcher/ViewSwitcher';
import AdminHierarchyFilter from '../../atoms/AdminHierarchyFilter/AdminHierarchyFilter';
import ActivityInsightsChart from '../../molecules/charts/ActivityInsightsChart/ActivityInsightsChart';
import DonutChart from '../../molecules/charts/DonutChart/DonutChart';
import DrillDownPanel from '../../molecules/charts/DrillDownPanel/DrillDownPanel';

import {
  exportEchartsPNG,
  exportRowsAsCSV,
  exportPageAsPDF,
} from '../../../utils/exportChartsUtils';

import './SecufireActionLogs.css';

export const MS_CONFIGS = {
  'historic-fire_service': {
    label: 'Historique des feux',
    Icon: LocalFireDepartmentOutlined,
    color: '#be1622',
    bg: 'rgba(190,22,34,0.07)',
  },

  'active-fire_service': {
    label: 'Points chauds',
    Icon: WhatshotOutlined,
    color: '#EAB308',
    bg: 'rgba(234,179,8,0.09)',
  },

  physical_model: {
    label: 'Modèle physique',
    Icon: PsychologyOutlined,
    color: '#7C3AED',
    bg: 'rgba(124,58,237,0.07)',
  },

  dfci_service: {
    label: 'Analyse terrain',
    Icon: ShieldOutlined,
    color: '#F97316',
    bg: 'rgba(249,115,22,0.07)',
  },

  vegetation_service: {
    label: 'Végétation',
    Icon: ForestOutlined,
    color: '#16A34A',
    bg: 'rgba(22,163,74,0.07)',
  },

  weather_service: {
    label: 'Météo',
    Icon: WaterOutlined,
    color: '#0284C7',
    bg: 'rgba(2,132,199,0.07)',
  },
};

const MS_ORDER = [
  'historic-fire_service',
  'active-fire_service',
  'physical_model',
  'dfci_service',
  'vegetation_service',
  'weather_service',
];

const VIEW_MODES = [
  { key: 'daily', label: 'Jour' },
  { key: 'weekly', label: 'Semaine' },
  { key: 'monthly', label: 'Mois' },
];

const DRILL_MODES = [
  { key: 'endpoint', label: 'Par endpoint' },
  { key: 'user', label: 'Par utilisateur' },
];

const SecufireActionLogs = ({
  data,
  loading,
  startDate,
  endDate,
  onDateChange,

  users = [],

  selectedMs,
  onMsSelect,

  onEndpointDrill,
  endpointDrillData,
  onEndpointDrillClose,

  onUserDrill,
  userDrillData,
  onUserDrillClose,

  admins = [],
  selectedAdminId,
  onAdminSelect,

  onRefresh,
  refreshing = false,

  notifSlot,

  isInternal,
  onIsInternalChange,
}) => {
  const [viewMode, setViewMode] = useState('daily');
  const [drillMode, setDrillMode] = useState('endpoint');
  const [drillSearch, setDrillSearch] = useState('');

  const pageRef = useRef(null);
  const activityChartRef = useRef(null);
  const donutChartRef = useRef(null);
  const drillDownRef = useRef(null);

  /*
   * Important :
   * On ferme le détail lorsque les données principales changent.
   *
   * Cela couvre notamment :
   * - refresh
   * - changement de période
   * - changement d'admin
   * - changement de filtre interne/externe
   * - nouveau chargement des données
   *
   * Le parent reste propriétaire de endpointDrillData/userDrillData,
   * mais on lui demande explicitement de vider la sélection.
   */
  const previousDataRef = useRef(data);

  useEffect(() => {
    const previousData = previousDataRef.current;

    if (previousData !== data) {
      onEndpointDrillClose?.();
      onUserDrillClose?.();
    }

    previousDataRef.current = data;
  }, [
    data,
    onEndpointDrillClose,
    onUserDrillClose,
  ]);

  /*
   * Si la période change, on nettoie immédiatement le détail.
   * Ainsi, même pendant le chargement des nouvelles données,
   * l'ancien historique ne reste pas affiché.
   */
  const previousPeriodRef = useRef({
    startDate,
    endDate,
  });

  useEffect(() => {
    const previous = previousPeriodRef.current;

    if (
      previous.startDate !== startDate ||
      previous.endDate !== endDate
    ) {
      onEndpointDrillClose?.();
      onUserDrillClose?.();
    }

    previousPeriodRef.current = {
      startDate,
      endDate,
    };
  }, [
    startDate,
    endDate,
    onEndpointDrillClose,
    onUserDrillClose,
  ]);

  /*
   * Changement de microservices sélectionnés :
   * le détail précédent n'est plus forcément valide.
   */
  const previousSelectedMsRef = useRef(null);

  useEffect(() => {
    const current = JSON.stringify(
      selectedMs instanceof Set
        ? [...selectedMs].sort()
        : Array.isArray(selectedMs)
          ? [...selectedMs].sort()
          : selectedMs
    );

    if (
      previousSelectedMsRef.current !== null &&
      previousSelectedMsRef.current !== current
    ) {
      onEndpointDrillClose?.();
      onUserDrillClose?.();
    }

    previousSelectedMsRef.current = current;
  }, [
    selectedMs,
    onEndpointDrillClose,
    onUserDrillClose,
  ]);

  /*
   * Refresh :
   * on vide le drill immédiatement AVANT de lancer le refresh.
   *
   * Cela évite que l'ancien historique reste visible pendant
   * le temps du nouveau chargement.
   */
  const handleRefresh = () => {
    onEndpointDrillClose?.();
    onUserDrillClose?.();

    onRefresh?.();
  };

  const selectedMsSet = useMemo(() => {
    if (!selectedMs) {
      return new Set(MS_ORDER);
    }

    if (selectedMs instanceof Set) {
      return selectedMs.size > 0
        ? selectedMs
        : new Set(MS_ORDER);
    }

    if (Array.isArray(selectedMs)) {
      return new Set(
        selectedMs.length > 0
          ? selectedMs
          : MS_ORDER
      );
    }

    return new Set([selectedMs]);
  }, [selectedMs]);

  const handleMsToggle = (msKey) => {
    const next = new Set(selectedMsSet);

    if (next.has(msKey)) {
      if (next.size > 1) {
        next.delete(msKey);
      }
    } else {
      next.add(msKey);
    }

    /*
     * Le détail actuel peut appartenir à un microservice
     * qui vient d'être désélectionné.
     */
    const currentEndpointMs = endpointDrillData?.microservice;
    const currentUserDrill = userDrillData;

    if (
      currentEndpointMs &&
      !next.has(currentEndpointMs)
    ) {
      onEndpointDrillClose?.();
    }

    if (currentUserDrill) {
      onUserDrillClose?.();
    }

    onMsSelect(next);
  };

  const {
    totalCalls,
    activeMsCount,
    topMs,
  } = useMemo(() => {
    const list = data?.microservices || [];

    const totalCalls = list.reduce(
      (sum, m) => sum + (m.total_usage || 0),
      0
    );

    const activeMsCount = selectedMsSet.size;

    const selectedList = list.filter(
      (m) => selectedMsSet.has(m.microservice)
    );

    const topMs = selectedList.reduce(
      (best, m) =>
        !best || m.total_usage > best.total_usage
          ? m
          : best,
      null
    );

    return {
      totalCalls,
      activeMsCount,
      topMs,
    };
  }, [
    data,
    selectedMsSet,
  ]);

  const adaptedMicroservices = useMemo(
    () =>
      (data?.microservices || [])
        .filter((m) =>
          selectedMsSet.has(m.microservice)
        )
        .map((m) => ({
          ...m,
          color:
            MS_CONFIGS[m.microservice]?.color,
        })),
    [
      data,
      selectedMsSet,
    ]
  );

  const mergedDailyTrend = useMemo(() => {
    const map = {};

    (data?.microservices || [])
      .filter((m) =>
        selectedMsSet.has(m.microservice)
      )
      .forEach((m) => {
        (m.daily_trend || []).forEach((d) => {
          const key = (
            d.date ||
            d.day ||
            ''
          ).slice(0, 10);

          if (!key) return;

          if (!map[key]) {
            map[key] = {
              date: key,
              total_activities: 0,
            };
          }

          map[key].total_activities +=
            d.total_activities ??
            d.count ??
            0;
        });
      });

    return Object.values(map).sort(
      (a, b) =>
        a.date.localeCompare(b.date)
    );
  }, [
    data,
    selectedMsSet,
  ]);

  const handleExportTrendPNG = () =>
    exportEchartsPNG(
      activityChartRef,
      'evolution-temporelle-secufire'
    );

  const handleExportTrendCSV = () =>
    exportRowsAsCSV(
      mergedDailyTrend.map((d) => ({
        date: d.date,
        total_activites: d.total_activities,
      })),
      'evolution-temporelle-secufire'
    );

  const handleExportDonutPNG = () =>
    exportEchartsPNG(
      donutChartRef,
      'repartition-microservices'
    );

  const handleExportDonutCSV = () =>
    exportRowsAsCSV(
      (data?.microservices || [])
        .filter((m) =>
          selectedMsSet.has(m.microservice)
        )
        .map((m) => ({
          microservice:
            MS_CONFIGS[m.microservice]?.label ||
            m.microservice,
          appels: m.total_usage || 0,
        })),
      'repartition-microservices'
    );

  const handleExportDrillPNG = () =>
    exportEchartsPNG(
      drillDownRef,
      `detail-${drillMode}`
    );

  const handleExportDrillCSV = () =>
    exportRowsAsCSV(
      drillDownRef.current?.getCSVRows?.(),
      `detail-${drillMode}`
    );

  const handleExportPDF = () =>
    exportPageAsPDF(
      pageRef.current,
      'logs-secufire-action'
    );

  if (loading && !refreshing) {
    return (
      <div className="sal-loading">
        <div className="sal-spinner" />
        <p>
          Chargement des logs SecuFire Action…
        </p>
      </div>
    );
  }

  if (!data) {
    return null;
  }

  const selectedAdminName =
    admins.find(
      (a) => a.id === selectedAdminId
    )?.username || null;

  return (
    <div
      className="sal-wrapper"
      ref={pageRef}
    >
      {/* HEADER */}
      <div className="sal-header">
        <div className="sal-stats-block">
          <div className="sal-kpis-row">
            <StatCard
              icon={<BoltOutlined />}
              label="Total Appels"
              value={totalCalls.toLocaleString()}
              variant="red"
            />

            <StatCard
              icon={<TrendingUpOutlined />}
              label="Service dominant"
              value={
                topMs
                  ? MS_CONFIGS[
                      topMs.microservice
                    ]?.label ||
                    topMs.microservice
                  : 'N/A'
              }
              variant="orange"
              small
            />
          </div>
        </div>

        <div className="header-sep" />

        <div className="sal-filters-block">
          <div className="sal-filter-group">
            <span className="sal-filter-label">
              Admin :
            </span>

            <AdminHierarchyFilter
              admins={admins}
              selectedAdminId={selectedAdminId}
              onSelect={(value) => {
                onEndpointDrillClose?.();
                onUserDrillClose?.();
                onAdminSelect?.(value);
              }}
            />
          </div>

          <div className="sal-filter-group">
            <span className="sal-filter-label">
              Comptes :
            </span>

            <select
              value={
                isInternal === null
                  ? 'all'
                  : isInternal
                    ? 'internal'
                    : 'external'
              }
              onChange={(e) => {
                const v =
                  e.target.value;

                onEndpointDrillClose?.();
                onUserDrillClose?.();

                onIsInternalChange(
                  v === 'all'
                    ? null
                    : v === 'internal'
                      ? true
                      : false
                );
              }}
              className="sal-internal-select"
            >
              <option value="all">
                Tous
              </option>
              <option value="external">
                Externes
              </option>
              <option value="internal">
                Internes
              </option>
            </select>
          </div>

          <div className="sal-filter-group">
            <span className="sal-filter-label">
              Période :
            </span>

            <DateRangePicker
              startDate={startDate}
              endDate={endDate}
              onStartChange={(date) => {
                onEndpointDrillClose?.();
                onUserDrillClose?.();
                onDateChange(
                  date,
                  endDate
                );
              }}
              onEndChange={(date) => {
                onEndpointDrillClose?.();
                onUserDrillClose?.();
                onDateChange(
                  startDate,
                  date
                );
              }}
            />
          </div>
        </div>

        <div className="header-sep" />

        <div className="sal-actions-slot">
          <button
            className={`sal-refresh-btn${
              refreshing
                ? ' sal-refresh-btn--loading'
                : ''
            }`}
            onClick={handleRefresh}
            disabled={refreshing}
            title="Rafraîchir"
          >
            <RefreshOutlined
              style={{ fontSize: 14 }}
            />
            <span>Rafraîchir</span>
          </button>

          {notifSlot}
        </div>
      </div>

      {/* ADMIN BADGE */}
      {selectedAdminId &&
        selectedAdminName && (
          <div className="sal-admin-badge">
            <span className="sal-admin-badge-dot" />

            <span className="sal-admin-badge-text">
              Données filtrées · hiérarchie de{' '}
              <strong>
                {selectedAdminName}
              </strong>
            </span>
          </div>
        )}

      {/* INTERNAL BADGE */}
      {isInternal !== null && (
        <div
          className="sal-admin-badge"
          style={{
            background: isInternal
              ? '#f0fdf4'
              : '#eff6ff',
            borderColor: isInternal
              ? '#86efac'
              : '#93c5fd',
          }}
        >
          <span
            className="sal-admin-badge-dot"
            style={{
              background: isInternal
                ? '#16a34a'
                : '#2563eb',
            }}
          />

          <span className="sal-admin-badge-text">
            Filtre actif · comptes{' '}
            <strong
              style={{
                color: isInternal
                  ? '#16a34a'
                  : '#2563eb',
              }}
            >
              {isInternal
                ? 'internes'
                : 'externes'}
            </strong>{' '}
            uniquement
          </span>
        </div>
      )}

      {/* MICRO-SERVICES */}
      <div className="sal-ms-switcher">
        {MS_ORDER.map((msKey) => {
          const cfg =
            MS_CONFIGS[msKey];

          const msD =
            data.microservices?.find(
              (m) =>
                m.microservice ===
                msKey
            );

          const calls =
            msD?.total_usage || 0;

          const isActive =
            selectedMsSet.has(msKey);

          return (
            <button
              key={msKey}
              className={`sal-ms-tab${
                isActive
                  ? ' sal-ms-tab--active'
                  : ''
              }`}
              style={{
                '--ms-color':
                  cfg.color,
                '--ms-bg':
                  cfg.bg,
              }}
              onClick={() =>
                handleMsToggle(msKey)
              }
            >
              <cfg.Icon
                sx={{
                  fontSize: 16,
                }}
              />

              <span className="sal-ms-tab-label">
                {cfg.label}
              </span>

              <span className="sal-ms-tab-count">
                {calls.toLocaleString()}
              </span>

              {isActive && (
                <span className="sal-ms-tab-check">
                  ✓
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* TREND */}
      <div className="sal-section-label">
        Tendance d'utilisation
      </div>

      <div className="sal-top-row">
        <div className="sal-chart-card sal-top-card">
          <div className="sal-chart-header--row">
            <div>
              <h3 className="sal-chart-title">
                Évolution temporelle
              </h3>

              <span className="sal-chart-subtitle">
                {[...selectedMsSet].map(
                  (k, i) => (
                    <React.Fragment
                      key={k}
                    >
                      {i > 0 && (
                        <span
                          style={{
                            color:
                              '#94a3b8',
                          }}
                        >
                          {' '}
                          +{' '}
                        </span>
                      )}

                      <span
                        style={{
                          color:
                            MS_CONFIGS[
                              k
                            ]?.color,
                          fontWeight: 700,
                        }}
                      >
                        {
                          MS_CONFIGS[k]
                            ?.label
                        }
                      </span>
                    </React.Fragment>
                  )
                )}
              </span>
            </div>

            <ViewSwitcher
              modes={VIEW_MODES}
              value={viewMode}
              onChange={setViewMode}
              color="#be1622"
            />
          </div>

          <div className="sal-chart-body">
            <ActivityInsightsChart
              ref={activityChartRef}
              dailyTrend={
                mergedDailyTrend
              }
              microservices={
                adaptedMicroservices
              }
              startDate={startDate}
              endDate={endDate}
              viewMode={viewMode}
            />
          </div>
        </div>

        <div className="sal-chart-card sal-top-card">
          <div
            className="sal-chart-header"
            style={{
              display: 'flex',
              alignItems:
                'flex-start',
              justifyContent:
                'space-between',
            }}
          >
            <div>
              <h3 className="sal-chart-title">
                Répartition des microservices
              </h3>

              <span className="sal-chart-subtitle">
                Part de chaque microservice
              </span>
            </div>
          </div>

          <div className="sal-chart-body sal-donut-body">
            <DonutChart
              ref={donutChartRef}
              microservices={
                data.microservices ||
                []
              }
              selectedMsSet={
                selectedMsSet
              }
              totalCalls={
                totalCalls
              }
            />
          </div>
        </div>
      </div>

      {/* DRILL DOWN */}
      <div className="sal-section-label">
        Détail par service
      </div>

      <div className="sal-chart-card sal-chart-card--drill">
        <div className="sal-drill-header">
          <div className="sal-drill-header-top">
            <div className="sal-drill-header-left">
              <h3 className="sal-chart-title">
                Détail par service
              </h3>

              <ViewSwitcher
                modes={DRILL_MODES}
                value={drillMode}
                onChange={(m) => {
                  onEndpointDrillClose?.();
                  onUserDrillClose?.();

                  setDrillMode(m);
                  setDrillSearch('');
                }}
                color="#be1622"
              />
            </div>

            <div
              style={{
                display: 'flex',
                alignItems:
                  'center',
                gap: 8,
              }}
            >
              <div
                className={`sal-drill-search${
                  drillSearch
                    ? ' sal-drill-search--active'
                    : ''
                }`}
              >
                <SearchOutlined className="sal-drill-search__icon" />

                <input
                  type="text"
                  className="sal-drill-search__input"
                  placeholder={
                    drillMode ===
                    'endpoint'
                      ? 'Nom d’endpoint…'
                      : 'Nom d’utilisateur…'
                  }
                  value={
                    drillSearch
                  }
                  onChange={(e) =>
                    setDrillSearch(
                      e.target.value
                    )
                  }
                />

                {drillSearch && (
                  <button
                    className="sal-drill-search__clear"
                    onClick={() =>
                      setDrillSearch('')
                    }
                    title="Effacer"
                  >
                    <CloseOutlined
                      style={{
                        fontSize: 13,
                      }}
                    />
                  </button>
                )}
              </div>
            </div>
          </div>

          <span className="sal-drill-hint">
            {drillMode === 'endpoint'
              ? `${[
                  ...selectedMsSet,
                ]
                  .map(
                    (k) =>
                      MS_CONFIGS[k]
                        ?.label
                  )
                  .join(
                    ' + '
                  )} · cliquer sur un endpoint`
              : 'Cliquer sur un utilisateur pour voir son historique'}
          </span>
        </div>

        <div className="sal-chart-body sal-chart-body--drill">
          <DrillDownPanel
            ref={drillDownRef}
            mode={drillMode}
            searchQuery={
              drillSearch
            }
            selectedMsSet={
              selectedMsSet
            }
            msData={
              data.microservices ||
              []
            }
            users={users}
            onEndpointDrill={
              onEndpointDrill
            }
            onUserDrill={
              onUserDrill
            }
            endpointDrillData={
              endpointDrillData
            }
            userDrillData={
              userDrillData
            }
            onEndpointDrillClose={
              onEndpointDrillClose
            }
            onUserDrillClose={
              onUserDrillClose
            }
          />
        </div>
      </div>
    </div>
  );
};

export default SecufireActionLogs;
